import { createContext, useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import UIIcon from "common/blocks/UIIcon";
import ReactModal from "react-modal";

import styles from "./Popup.module.scss";

const cx = classNames.bind(styles);

process.env.NODE_ENV !== "test" && ReactModal.setAppElement("#__next");

export interface PopupProps {
    children?: React.ReactNode;
    contentLabel: string;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onAfterClose?: () => void;
    onAfterOpen?: () => void;
    onRequestClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
    isSideModal?: boolean;
    className?: string;
}

const Popup = (props: PopupProps) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const {
        children,
        contentLabel,
        isOpen,
        setIsOpen,
        onAfterClose,
        onAfterOpen,
        onRequestClose,
        isSideModal,
        className,
    } = props;

    const preventDefault = (e: TouchEvent) => {
        if (!popupRef.current?.contains(e.target as Node)) e.preventDefault();
    };

    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.addEventListener("touchmove", preventDefault, { passive: false });
        } else {
            document.body.removeEventListener("touchmove", preventDefault);
        }

        return () => {
            document.body.removeEventListener("touchmove", preventDefault);
        };
    }, [isOpen]);

    function handleAfterOpen() {
        onAfterOpen?.();
    }

    function handleAfterClose() {
        onAfterClose?.();
        setIsClosing(false);
    }

    function handleRequestClose(e: React.MouseEvent | React.KeyboardEvent) {
        onRequestClose?.(e);
        setIsOpen(false);
        setIsClosing(true);
    }

    const args = {
        className: cx("modalContent", className),
        overlayClassName: cx("overlay"),
        portalClassName: cx("modal", { isOpen, isClosing }, { sideModal: isSideModal }),
        isOpen,
        closeTimeoutMS: 450,
        contentLabel: contentLabel,
        onAfterClose: handleAfterClose,
        onAfterOpen: handleAfterOpen,
        onRequestClose: handleRequestClose,
        bodyOpenClassName: cx("bodyOpen"),
        shouldFocusAfterRender: false,
    };

    const PopupContext = createContext(onRequestClose);

    return (
        <div className={cx("section")}>
            {/*  @ts-ignore */}
            <ReactModal {...args}>
                <div ref={popupRef}>
                    {isSideModal && (
                        <button
                            onClick={handleRequestClose}
                            className={cx("closeIcon")}
                            title="Close Modal"
                            aria-label="Close Modal"
                        >
                            <UIIcon icon="Close" />
                        </button>
                    )}
                    <div className={cx("contentWrap")}>
                        <div className={cx("content")}>
                            <PopupContext.Provider value={onRequestClose}>
                                {children}
                            </PopupContext.Provider>
                        </div>
                    </div>
                </div>
            </ReactModal>
        </div>
    );
};

export default Popup;
